<script setup lang="ts">
interface BridgeLink {
  label: string;
  href: string;
}

interface BridgeBlock {
  heading: string;
  line: string;
  listing_ids?: number[];
  links?: BridgeLink[];
}

const props = defineProps<{
  block: BridgeBlock;
}>();

const { locale } = useI18n();
const localePath = useLocalePath();
const config = useRuntimeConfig();
const whatsappNumber = config.public.whatsappNumber as string;
const telegramLink = (config.public.telegramLink as string) || "";

const { data: apartments } = await useAsyncData(
  `amenities_bridge_apartments-${locale.value}`,
  async () => {
    const collection = ("apartments_" + locale.value) as "apartments_en";
    const content = await queryCollection(collection).all();
    return content;
  },
);

const { data: fromPrices } = await useFetch<Record<number, number>>(
  '/api/apartments/from-prices',
  { default: () => ({}) },
);

type GuestFacts = { capacity: number; threshold: number; extraGuestUsd?: number };
const { data: guestFactsMap } = await useFetch<Record<number, GuestFacts>>(
  '/api/apartments/facts',
  { default: () => ({}) },
);

const apartmentsData = computed(() => {
  const visible = (apartments.value || [])
    // Hide apartments not publicly visible: `guestFactsMap` keys are the
    // server's active && listed set — the catalog allowlist.
    .filter((item) => guestFactsMap.value[Number(item.listing_id)]);
  const wanted = props.block.listing_ids;
  const picked = wanted?.length
    ? wanted
        .map((id) => visible.find((item) => Number(item.listing_id) === id))
        .filter((item) => item !== undefined)
    : visible.slice(0, 3);
  return picked.slice(0, 3).map((item) => ({
    listing_id: item.listing_id || "",
    title: item.title || "",
    price: fromPrices.value[Number(item.listing_id)] ?? null,
    capacity: item.capacity,
    rooms: item.rooms,
    area_sqm: item.area_sqm,
    view_type: item.view_type,
    floor: item.floor,
    cover_photo: item.cover_photo?.[0] || {
      url: "/images/placeholder-image.jpg",
      alt: "placeholder-image",
    },
    gallery: Array.isArray(item.photo_gallery) ? item.photo_gallery : [],
  }));
});
</script>

<template>
  <section
    v-if="apartmentsData.length"
    class="relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-500/10"
    />

    <div data-reveal class="relative mx-auto w-full max-w-7xl px-4 py-8 md:py-12">
      <header class="mb-6 md:mb-8">
        <h2
          class="max-w-5xl font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.heading }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          class="mt-3 max-w-3xl font-body text-base leading-relaxed text-slate-600 md:mt-4 md:text-lg dark:text-slate-300"
        >
          {{ block.line }}
        </p>
      </header>

      <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <ApartmentCard
          v-for="item in apartmentsData"
          :key="item.listing_id"
          :apartment="{
            listing_id: Number(item.listing_id),
            title: item.title,
            cover_photo: item.cover_photo,
            capacity: item.capacity,
            rooms: item.rooms,
            area_sqm: item.area_sqm,
            view_type: item.view_type,
            floor: item.floor,
          }"
          :gallery="item.gallery"
          :catalog-price="item.price"
          :locale="locale"
          :whatsapp-number="whatsappNumber"
          :telegram-link="telegramLink"
          :guest-facts="guestFactsMap[Number(item.listing_id)] ?? null"
        />
      </div>

      <div
        v-if="block.links?.length"
        class="mt-6 flex flex-wrap items-center gap-3 md:mt-8"
      >
        <NuxtLink
          v-for="link in block.links"
          :key="link.href"
          :to="localePath(link.href)"
          class="inline-flex items-center gap-1.5 rounded-full border border-amber-400/60 bg-amber-50 px-4 py-2 font-body text-sm font-semibold text-amber-800 shadow-sm transition-all duration-300 hover:border-amber-500 hover:bg-amber-100 hover:shadow-md dark:border-amber-400/40 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:bg-amber-900/40"
        >
          {{ link.label }}
          <svg
            class="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
